import { Compass, Eye, Gauge, Timer, Workflow, Layers } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ImageSlot from '../components/ImageSlot';
import ClinicForm from '../components/ClinicForm';
import { T } from '../i18n/copy';

const UNDERSTAND_ICONS = [Compass, Workflow, Layers, Gauge, Eye, Timer];

export default function ClinicPage({ lang }) {
  const t = T.clinicPage;
  return (
    <>
      <Seo lang={lang} title={T.meta.clinic.title[lang]} description={T.meta.clinic.desc[lang]} />

      <section data-header-theme="bone" data-testid="clinic-page-hero" className="editorial-margins bg-bone pb-16 pt-12 md:pt-16">
        <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <span className="inline-block h-2 w-2 bg-vermilion" aria-hidden="true" />
              <span className="eyebrow text-midnight/70">{t.eyebrow[lang]}</span>
              <span className="eyebrow ml-auto hidden text-steel-dark md:inline">01</span>
            </div>
            <h1 className="mt-8 max-w-2xl font-tight font-semibold text-[clamp(2.2rem,4.6vw,4.2rem)] leading-[1.04] tracking-[-0.02em] text-midnight" data-testid="clinic-page-headline">
              {t.headline[lang]}
            </h1>
            <Reveal delay={0.1} className="mt-8 max-w-md">
              <p className="text-base leading-relaxed text-midnight/70">{t.body[lang]}</p>
              <p className="mt-5 text-base leading-relaxed text-midnight/70">{t.body2[lang]}</p>
              <p className="mt-4 text-[15px] leading-relaxed text-midnight/85">
                {t.body2List[lang].map((item, i) => (
                  <span key={item}>
                    <span className="font-serif italic">{item}</span>
                    {i < t.body2List[lang].length - 1 && <span className="text-vermilion"> / </span>}
                  </span>
                ))}
              </p>
            </Reveal>
            <p className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="clinic-page-micro">
              <span className="inline-block h-2 w-2 bg-vermilion" aria-hidden="true" />
              <span className="eyebrow text-midnight/70">{t.micro[lang][0]}</span>
              <span className="eyebrow text-steel-dark">{t.micro[lang][1]}</span>
            </p>
          </div>
          <div className="lg:col-span-5">
            <ImageSlot slotKey="clinicPage" className="w-full" />
          </div>
        </div>
      </section>

      <section className="border-t border-midnight/10" data-testid="clinic-understand">
        <div className="editorial-margins py-16 md:py-20">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <h2 className="font-serif text-2xl text-midnight md:text-3xl" data-testid="clinic-understand-title">{t.understandTitle[lang]}</h2>
            <p className="text-base italic text-steel-dark">{t.understandNote[lang]}</p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-x-10 md:grid-cols-2">
            {t.understandList[lang].map((item, i) => {
              const Icon = UNDERSTAND_ICONS[i];
              return (
                <li key={item} className="flex items-center gap-4 border-t border-midnight/12 py-4" data-testid={`clinic-understand-item-${i}`}>
                  <Icon className="h-4 w-4 shrink-0 text-vermilion" strokeWidth={1.5} />
                  <span className="text-base text-midnight/80">{item}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section id="clinic-form" className="border-t border-midnight/10 bg-[#EDEAE0]" data-testid="clinic-form-section">
        <div className="editorial-margins py-16 md:py-24">
          <div className="mx-auto max-w-3xl">
            <p className="eyebrow text-midnight/70">{t.formLabel[lang]}</p>
            <ClinicForm lang={lang} />
            <p className="mt-5 text-[12.5px] leading-relaxed text-midnight/60" data-testid="clinic-page-nonguarantee">
              {T.form.nonGuarantee[lang]}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
